import z from 'zod';
import { AllPaymentProviderSchema } from '../common-tools';
import { AccountTypes } from '../user/business';
import { BusinessDataSchema } from '../user/web';
/**
 * Auth V3 — business device pairing.
 *
 * Shared contract between the backend and any pairing client (POS app, kiosk,
 * web POS, payment terminal, and the admin dashboard that approves them). A
 * device generates a key pair locally, an admin claims the pairing, and the
 * device proves possession of its private key by signing a challenge to receive
 * a Firebase custom token carrying `AccountTypes.BUSINESS_DEVICE`.
 */
/** Device types that can pair via Auth V3. */
export var BusinessDeviceType;
(function (BusinessDeviceType) {
    BusinessDeviceType["POS_DEVICE"] = "POS_DEVICE";
    BusinessDeviceType["POS_WEB_DEVICE"] = "POS_WEB_DEVICE";
    BusinessDeviceType["KIOSK"] = "KIOSK";
    BusinessDeviceType["KDS"] = "KDS";
    BusinessDeviceType["DISPLAY_DEVICE"] = "DISPLAY_DEVICE";
    BusinessDeviceType["PAYMENT_TERMINAL"] = "PAYMENT_TERMINAL";
})(BusinessDeviceType || (BusinessDeviceType = {}));
/**
 * Lifecycle of a pairing session, as reported by the poll endpoint.
 *
 * PENDING -> READY_FOR_COMPLETION -> VERIFIED -> COMPLETED
 *     |               |                |
 *     +-> EXPIRED     +-> EXPIRED      +-> EXPIRED (recovery deadline)
 *     +-> REJECTED
 */
export var V3DevicePairingStatus;
(function (V3DevicePairingStatus) {
    V3DevicePairingStatus["PENDING"] = "PENDING";
    V3DevicePairingStatus["READY_FOR_COMPLETION"] = "READY_FOR_COMPLETION";
    /** Proof accepted and provisioning committed; token delivery may be retried. */
    V3DevicePairingStatus["VERIFIED"] = "VERIFIED";
    V3DevicePairingStatus["COMPLETED"] = "COMPLETED";
    V3DevicePairingStatus["EXPIRED"] = "EXPIRED";
    V3DevicePairingStatus["REJECTED"] = "REJECTED";
})(V3DevicePairingStatus || (V3DevicePairingStatus = {}));
export const BusinessDeviceTypeSchema = z.nativeEnum(BusinessDeviceType);
export const V3DevicePairingStatusSchema = z.nativeEnum(V3DevicePairingStatus);
/**
 * Stable Auth V3 failure classifications. Clients use these values to choose a
 * retry, reauthentication, support, or fresh-pairing recovery action.
 */
export var AuthV3ErrorClassification;
(function (AuthV3ErrorClassification) {
    AuthV3ErrorClassification["PAIRING_NOT_FOUND_OR_EXPIRED"] = "PAIRING_NOT_FOUND_OR_EXPIRED";
    AuthV3ErrorClassification["PAIRING_ALREADY_CLAIMED"] = "PAIRING_ALREADY_CLAIMED";
    AuthV3ErrorClassification["PAIRING_PROOF_INVALID"] = "PAIRING_PROOF_INVALID";
    AuthV3ErrorClassification["PAIRING_PROOF_MISMATCH"] = "PAIRING_PROOF_MISMATCH";
    AuthV3ErrorClassification["DEVICE_OWNED_BY_OTHER_BUSINESS"] = "DEVICE_OWNED_BY_OTHER_BUSINESS";
    AuthV3ErrorClassification["DEVICE_REVOKED"] = "DEVICE_REVOKED";
    AuthV3ErrorClassification["DEVICE_TYPE_CONFLICT"] = "DEVICE_TYPE_CONFLICT";
    AuthV3ErrorClassification["TERMINAL_OWNED_BY_OTHER_BUSINESS"] = "TERMINAL_OWNED_BY_OTHER_BUSINESS";
    AuthV3ErrorClassification["TERMINAL_INACTIVE"] = "TERMINAL_INACTIVE";
    AuthV3ErrorClassification["LOCATION_TERMINAL_CONFLICT"] = "LOCATION_TERMINAL_CONFLICT";
    AuthV3ErrorClassification["BUSINESS_DEVICE_MANAGE_FORBIDDEN"] = "BUSINESS_DEVICE_MANAGE_FORBIDDEN";
    AuthV3ErrorClassification["TOKEN_MINT_TEMPORARY_FAILURE"] = "TOKEN_MINT_TEMPORARY_FAILURE";
    AuthV3ErrorClassification["DEVICE_NOT_FOUND"] = "DEVICE_NOT_FOUND";
    AuthV3ErrorClassification["DEVICE_KEY_MISMATCH"] = "DEVICE_KEY_MISMATCH";
})(AuthV3ErrorClassification || (AuthV3ErrorClassification = {}));
export const AuthV3ErrorClassificationSchema = z.nativeEnum(AuthV3ErrorClassification);
export const AuthV3ErrorResponseSchema = z
    .object({
    errorCode: AuthV3ErrorClassificationSchema,
    supportReference: z.string().min(1).optional(),
})
    .strict();
/** Device signing algorithms supported in the first pass. */
export const DeviceSignatureAlgorithmSchema = z.enum(['P-256', 'Ed25519']);
// ── Endpoint 1: device starts pairing (POST /api/auth/v3/devices/pair) ──
export const DevicePairStartRequestSchema = z.object({
    deviceType: BusinessDeviceTypeSchema,
    /** Base64-encoded SPKI DER public key. */
    publicKey: z.string().min(1),
    publicKeyAlgorithm: DeviceSignatureAlgorithmSchema,
    serialNumber: z.string().min(1).optional(),
    deviceFingerprint: z.string().min(1).optional(),
    metadata: z.record(z.unknown()).optional(),
});
export const DevicePairStartResponseSchema = z.object({
    pairingCode: z.string(),
    pollingId: z.string(),
    expiresAt: z.string(),
});
// ── Endpoint 2: device polls status (GET /api/auth/v3/devices/pair/:pollingId) ──
export const DevicePairPollResponseSchema = z.object({
    status: V3DevicePairingStatusSchema,
    /** Present only when status is READY_FOR_COMPLETION. */
    challenge: z.string().optional(),
    challengeExpiresAt: z.string().optional(),
});
// ── Endpoint 3: device completes pairing (POST /api/auth/v3/devices/pair/complete) ──
export const DevicePairCompleteRequestSchema = z.object({
    pollingId: z.string().uuid(),
    /** Base64-encoded raw signature over the challenge bytes. */
    signature: z.string().min(1),
});
export const DevicePairCompleteResponseSchema = z.object({
    tokenType: z.literal('FIREBASE_CUSTOM_TOKEN'),
    customToken: z.string(),
    businessDeviceId: z.string(),
    businessId: z.string(),
    businessLocationId: z.string().optional(),
    paymentTerminalId: z.string().optional(),
    deviceType: BusinessDeviceTypeSchema,
});
// ── Refresh: paired device re-authenticates on cold start (no admin step) ──
//
// A previously-paired device gets a fresh Firebase custom token without re-running
// the admin pairing-code flow. Recognition alone is never enough: the device must
// prove it still owns the private key by signing a fresh, short-lived, single-use
// challenge.
//
//   POST /api/auth/v3/devices/refresh/start    → mint a challenge for a known device
//   POST /api/auth/v3/devices/refresh/complete → verify the signature, mint a token
export const DeviceRefreshStartRequestSchema = z
    .object({
    /** Either this or `publicKeyFingerprint` must identify the paired device. */
    businessDeviceId: z.string().uuid().optional(),
    publicKeyFingerprint: z.string().min(1).optional(),
    /** Best-effort hints only — never used for authentication. */
    deviceFingerprint: z.string().min(1).optional(),
    serialNumber: z.string().min(1).optional(),
})
    .refine((data) => Boolean(data.businessDeviceId || data.publicKeyFingerprint), {
    message: 'Either businessDeviceId or publicKeyFingerprint is required',
});
export const DeviceRefreshStartResponseSchema = z.object({
    pollingId: z.string(),
    /** Public nonce the device signs with its private key. */
    challenge: z.string(),
    challengeExpiresAt: z.string(),
});
export const DeviceRefreshCompleteRequestSchema = z.object({
    pollingId: z.string().uuid(),
    /** Base64-encoded raw signature over the challenge bytes. */
    signature: z.string().min(1),
});
/** Same shape as `DevicePairCompleteResponse`, kept as a distinct named contract. */
export const DeviceRefreshCompleteResponseSchema = z.object({
    tokenType: z.literal('FIREBASE_CUSTOM_TOKEN'),
    customToken: z.string(),
    businessDeviceId: z.string(),
    businessId: z.string(),
    businessLocationId: z.string().optional(),
    paymentTerminalId: z.string().optional(),
    deviceType: BusinessDeviceTypeSchema,
});
// ── Endpoint 4: admin claims/approves pairing (POST /api/admin/auth/v3/devices/claim) ──
/** Admin-supplied payment terminal data, applied when a PAYMENT_TERMINAL completes. */
export const DevicePairAdminTerminalSchema = z.object({
    terminalId: z.string().min(1).optional(),
    serialNumber: z.string().optional(),
    label: z.string().optional(),
    provider: z.string().optional(),
    metadata: z.record(z.unknown()).optional(),
    stripeLocationId: z.string().optional(),
});
const DevicePairingCodeSchema = z
    .string()
    .length(6)
    .regex(/^\d+$/, 'Pairing code must be 6 digits');
/** Explicit allowlist of device-provided details safe for an approver to view. */
export const DevicePairSafeMetadataSchema = z
    .object({
    platform: z.string().optional(),
    appVersion: z.string().optional(),
    model: z.string().optional(),
    deviceName: z.string().optional(),
})
    .strict();
export const DevicePairMerchantPreviewRequestSchema = z
    .object({ pairingCode: DevicePairingCodeSchema })
    .strict();
export const DevicePairMerchantPreviewResponseSchema = z
    .object({
    status: V3DevicePairingStatusSchema,
    expiresAt: z.string(),
    deviceType: BusinessDeviceTypeSchema,
    metadata: DevicePairSafeMetadataSchema,
})
    .strict();
/** Merchant-editable terminal assignment fields; arbitrary metadata is excluded. */
export const DevicePairMerchantTerminalSchema = DevicePairAdminTerminalSchema.omit({
    metadata: true,
}).strict();
export const DevicePairMerchantClaimRequestSchema = z
    .object({
    pairingCode: DevicePairingCodeSchema,
    businessLocationId: z.string().uuid().optional(),
    /** Existing PointCraft All_Payment_Terminals.id selected for a POS/Web POS device. */
    paymentTerminalId: z.string().uuid().optional(),
    paymentTerminal: DevicePairMerchantTerminalSchema.optional(),
})
    .strict();
// ── Migration: authenticated legacy POS session converts to Auth V3 device auth ──
//
// A POS device that is already signed in with the legacy business/terminal auth
// flow can use this endpoint to create/reuse its Auth V3 Business_Device record
// and receive a BUSINESS_DEVICE Firebase custom token. Unlike first-time pairing,
// this route is authenticated by the current session and does not need an admin
// pairing code.
export const DeviceMigrationPaymentTerminalSchema = DevicePairAdminTerminalSchema.extend({
    /** Existing All_Payment_Terminals.id when the legacy session already knows it. */
    id: z.string().uuid().optional(),
});
export const DeviceMigrationRequestSchema = z.object({
    /** Base64-encoded SPKI DER public key for the device's Auth V3 identity. */
    publicKey: z.string().min(1),
    publicKeyAlgorithm: DeviceSignatureAlgorithmSchema,
    deviceType: BusinessDeviceTypeSchema,
    /** Optional current location. Backend validates it belongs to the auth business. */
    businessLocationId: z.string().uuid().optional(),
    /** Optional previous location hint for audit/debug metadata. */
    previousBusinessLocationId: z.string().uuid().optional(),
    /** Existing All_Payment_Terminals.id, if known from the legacy session/app state. */
    paymentTerminalId: z.string().uuid().optional(),
    paymentTerminal: DeviceMigrationPaymentTerminalSchema.optional(),
    /** Best-effort hints only; the authenticated session is the migration proof. */
    deviceFingerprint: z.string().min(1).optional(),
    serialNumber: z.string().optional(),
    existingDeviceId: z.string().optional(),
    metadata: z.record(z.unknown()).optional(),
});
export const DeviceMigrationResponseSchema = z.object({
    tokenType: z.literal('FIREBASE_CUSTOM_TOKEN'),
    customToken: z.string(),
    accountType: z.literal(AccountTypes.BUSINESS_DEVICE),
    firebaseUid: z.string(),
    businessDeviceId: z.string(),
    businessId: z.string(),
    businessLocationId: z.string().optional(),
    paymentTerminalId: z.string().optional(),
    deviceType: BusinessDeviceTypeSchema,
    migrated: z.boolean(),
});
/** Backend-authoritative eligibility result for automatic legacy migration. */
export const DeviceMigrationEligibilityResponseSchema = z
    .object({ eligible: z.boolean() })
    .strict();
export const DevicePairPlatformClaimRequestSchema = z
    .object({
    pairingCode: DevicePairingCodeSchema,
    businessId: z.string().uuid(),
    /** Platform-admin-selected authoritative type for the completed device. */
    deviceType: BusinessDeviceTypeSchema,
    businessLocationId: z.string().uuid().optional(),
    paymentTerminal: DevicePairAdminTerminalSchema.optional(),
    metadata: z.record(z.unknown()).optional(),
})
    .strict();
/** @deprecated Alias retained for source compatibility. */
export const DevicePairLegacyAdminClaimRequestSchema = DevicePairPlatformClaimRequestSchema;
/** @deprecated Prefer `DevicePairPlatformClaimRequestSchema`. */
export const DevicePairAdminClaimRequestSchema = DevicePairPlatformClaimRequestSchema;
export const DevicePairAdminClaimResponseSchema = z.object({
    status: V3DevicePairingStatusSchema,
    pollingId: z.string(),
});
// ── Endpoint 5: device fetches its business info (GET /api/business/v3/devices) ──
/** Lifecycle status of a paired business device. */
export var BusinessDeviceStatus;
(function (BusinessDeviceStatus) {
    BusinessDeviceStatus["ACTIVE"] = "ACTIVE";
    BusinessDeviceStatus["REVOKED"] = "REVOKED";
})(BusinessDeviceStatus || (BusinessDeviceStatus = {}));
export const BusinessDeviceStatusSchema = z.nativeEnum(BusinessDeviceStatus);
/** Client-safe view of a paired `Business_Device` record (identity internals omitted). */
export const BusinessDeviceDetailsSchema = z.object({
    id: z.string(),
    businessId: z.string(),
    businessLocationId: z.string().nullable(),
    paymentTerminalId: z.string().nullable(),
    deviceType: BusinessDeviceTypeSchema,
    status: BusinessDeviceStatusSchema,
    serialNumber: z.string().nullable(),
    deviceFingerprint: z.string().nullable(),
    lastPairedAt: z.string().nullable(),
    metadata: z.record(z.unknown()).nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
/**
 * The payment account behind a terminal's provider, resolved from `All_Payment_Accounts`
 * by matching the terminal's `provider`. `null` when the device is not a terminal or no
 * matching account exists. Raw provider `metaData` is intentionally excluded.
 */
export const BusinessDeviceInfoPaymentAccountSchema = z.object({
    id: z.string(),
    provider: AllPaymentProviderSchema,
    providerAccountId: z.string().nullable(),
    serviceId: z.string(),
    enabled: z.boolean(),
    usesLocalTerminal: z.boolean(),
    currentBalance: z.number(),
});
/**
 * Response for `GET /api/business/v3/devices` — the bootstrap payload a paired device
 * needs: its business data/pricing, its own device record, and (for terminals) the
 * payment account behind its provider.
 */
export const BusinessDeviceInfoResponseSchema = z.object({
    business: BusinessDataSchema,
    device: BusinessDeviceDetailsSchema,
    paymentAccount: BusinessDeviceInfoPaymentAccountSchema.nullable(),
});
// ── Device activity: battery reporting ──────────────────────────────────────
//
// A paired device attaches a base64(UTF-8 JSON) "device context" payload to
// every authenticated request. The backend's activity hook reads the `battery`
// member of that payload and keeps the latest snapshot on
// `Business_Device.metadata.battery`; admin listings project it as flat fields.
/** Header carrying the base64-encoded device-context JSON. */
export const DEVICE_CONTEXT_DATA_HEADER = 'x-device-context-data';
/** Older name for the same value; clients send both. */
export const LEGACY_DEVICE_CONTEXT_DATA_HEADER = 'x-current-device-data';
/** Declares the payload encoding; `base64` is the only supported value. */
export const DEVICE_CONTEXT_DATA_ENCODING_HEADER = 'x-current-device-data-encoding';
export const DEVICE_CONTEXT_DATA_ENCODING = 'base64';
/**
 * The battery reading a client embeds in its device-context payload. Mirrors
 * the POS app's `DeviceBatteryMetadata`: `level` is a 0–100 percentage,
 * `lastChecked` is the epoch-millisecond device time of the reading (kept
 * unchanged while the client serves it from cache), `cache` is a fixed marker.
 */
export const DeviceContextBatterySchema = z.object({
    level: z.number().finite().min(0).max(100),
    isCharging: z.boolean().optional(),
    lastChecked: z.number().int().positive(),
    cache: z.literal('yes'),
});
/** Latest battery snapshot persisted under `Business_Device.metadata.battery`. */
export const BusinessDeviceBatterySchema = z.object({
    level: z.number().min(0).max(100),
    /** null when the platform reported no charging state. */
    isCharging: z.boolean().nullable(),
    /** ISO 8601 of the client's `lastChecked` (device clock). */
    lastCheckedAt: z.string().datetime({ offset: true }),
    /** ISO 8601 server receipt time of this snapshot. */
    updatedAt: z.string().datetime({ offset: true }),
});
/** Flat, nullable projection of the snapshot for admin listings (sortable columns). */
export const BusinessDeviceBatteryFieldsSchema = z.object({
    batteryLevel: z.number().min(0).max(100).nullable(),
    batteryIsCharging: z.boolean().nullable(),
    batteryLastCheckedAt: z.string().nullable(),
    batteryUpdatedAt: z.string().nullable(),
});
